/**
 * 💰 DRIVER EARNINGS CARD
 * Displays detailed earnings information for a specific driver
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  AccountBalance,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  TrendingUp,
  MonetizationOn,
  AccessTime,
} from '@mui/icons-material';
import { payoutsService } from '@/services/payoutsService';
import toast from 'react-hot-toast';

interface DriverEarningsCardProps {
  driverId: string;
  driverName?: string;
  compact?: boolean;
}

export const DriverEarningsCard: React.FC<DriverEarningsCardProps> = ({
  driverId,
  driverName,
  compact = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState<any>(null);

  useEffect(() => {
    loadEarnings();
  }, [driverId]);

  const loadEarnings = async () => {
    setLoading(true);
    try {
      const data = await payoutsService.getDriverEarnings(driverId);
      setEarnings(data);
    } catch (error: any) {
      console.error('Failed to load driver earnings:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load driver earnings');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => `₦${amount.toLocaleString()}`;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    return status === 'released' ? 'success' : 'warning';
  };

  const getStatusLabel = (status: string) => {
    return status === 'released' ? 'Released' : 'Locked';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!earnings) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">No earnings data available for this driver</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOn />
            {driverName ? `${driverName}'s Earnings` : 'Driver Earnings'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Detailed breakdown of earnings and withdrawal history
          </Typography>
        </Box>

        {/* Earnings Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'success.lighter',
                border: '1px solid',
                borderColor: 'success.light',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckIcon sx={{ color: 'success.main' }} />
                <Typography variant="body2" color="text.secondary">
                  Available Balance
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                {formatCurrency(earnings.availableBalance)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ready to withdraw
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'warning.lighter',
                border: '1px solid',
                borderColor: 'warning.light',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LockIcon sx={{ color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary">
                  Pending Earnings
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {formatCurrency(earnings.pendingEarnings)}
              </Typography>
              {earnings.releaseInfo.hoursUntilRelease !== null ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Releases in {earnings.releaseInfo.hoursUntilRelease} hours
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No pending releases
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Next Release Info */}
        {earnings.releaseInfo.nextReleaseAt && (
          <Alert severity="info" sx={{ mb: 3 }} icon={<ScheduleIcon />}>
            <Typography variant="body2">
              <strong>Next Release:</strong> {formatCurrency(earnings.releaseInfo.nextReleaseAmount)} on{' '}
              {formatDate(earnings.releaseInfo.nextReleaseAt)}
            </Typography>
          </Alert>
        )}

        {/* Additional Stats */}
        {!compact && (
          <>
            <Divider sx={{ my: 3 }} />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Pending Withdrawals
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(earnings.pendingWithdrawals)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {earnings.pendingWithdrawals > 0 ? 'Active withdrawal requests' : 'No active requests'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Withdrawn
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatCurrency(earnings.totalWithdrawn)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Successful payouts
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Lifetime Earnings
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatCurrency(earnings.totalEarnings)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    All-time total
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </>
        )}

        {/* Recent Bookings */}
        {!compact && earnings.recentBookings && earnings.recentBookings.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Bookings
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Booking #</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Release Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {earnings.recentBookings.map((booking: any) => (
                    <TableRow key={booking.bookingNumber} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {booking.bookingNumber}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(booking.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(booking.status)}
                          color={getStatusColor(booking.status) as any}
                          size="small"
                          icon={booking.status === 'released' ? <CheckIcon /> : <LockIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(booking.releaseDate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
};
